import { useCallback, useMemo, useRef } from 'react';
import SelectField from '@app/components/select-field';
import { T } from '@app/components/translation';
import { useT } from '@transifex/react';
import toast from '@app/components/toasts';
import { usePlaybookStore } from '@app/store/zustand-store/use-playbook-store';

import style from '../ai-review.module.scss';

type Option = {
  value: string;
  label: React.ReactNode;
  description: React.ReactNode;
};

const options: Option[] = [
  {
    value: 'paymentTerm',
    label: (
      <T
        _str="Payment term"
        _comment="Label for the payment term option in AI Review."
      />
    ),
    description: (
      <T
        _str="A payment term outlines the time frame in which a party is required to pay the invoice."
        _comment="Description of the payment term clause."
      />
    ),
  },
  {
    value: 'disputeResolution',
    label: (
      <T
        _str="Dispute resolution"
        _comment="Label for the dispute resolution option in AI Review."
      />
    ),
    description: (
      <T
        _str="A dispute resolution clause outlines the process for resolving disputes between parties."
        _comment="Description of the dispute resolution clause."
      />
    ),
  },
  {
    value: 'governingLaw',
    label: (
      <T
        _str="Governing law"
        _comment="Label for the governing law option in AI Review."
      />
    ),
    description: (
      <T
        _str="A governing law clause explains which law will govern the contract, which is crucial for dispute interpretation."
        _comment="Description of the governing law clause."
      />
    ),
  },
  {
    value: 'confidentiality',
    label: (
      <T
        _str="Confidentiality"
        _comment="Label for the confidentiality option in AI Review."
      />
    ),
    description: (
      <T
        _str="Check for the presence or absence of confidentiality clauses to protect sensitive information."
        _comment="Description of the confidentiality clause."
      />
    ),
  },
  {
    value: 'exclusionOfConsequentialLoss',
    label: (
      <T
        _str="Exclusion of consequential loss"
        _comment="Label for the exclusion of consequential loss option in AI Review."
      />
    ),
    description: (
      <T
        _str="Check for the presence or absence of clauses that exclude consequential losses."
        _comment="Description of the exclusion of consequential damages clause."
      />
    ),
  },
  {
    value: 'forceMajeure',
    label: (
      <T
        _str="Force majeure"
        _comment="Label for the force majeure option in AI Review."
      />
    ),
    description: (
      <T
        _str="Check for the presence or absence of force majeure clauses that cover unforeseen events."
        _comment="Description of the force majeure clause."
      />
    ),
  },
  {
    value: 'indemnities',
    label: (
      <T
        _str="Indemnities"
        _comment="Label for the indemnities option in AI Review."
      />
    ),
    description: (
      <T
        _str="Check for the presence or absence of indemnity clauses that specify compensation for losses or damages."
        _comment="Description of the indemnities clause."
      />
    ),
  },
  {
    value: 'limitationOfLiability',
    label: (
      <T
        _str="Limitation of liability"
        _comment="Label for the limitation of liability option in AI Review."
      />
    ),
    description: (
      <T
        _str="Check for the presence or absence of clauses that limit liability."
        _comment="Description of the limitation of liability clause."
      />
    ),
  },
  {
    value: 'liquidatedDamages',
    label: (
      <T
        _str="Liquidated damages"
        _comment="Label for the liquidated damages option in AI Review."
      />
    ),
    description: (
      <T
        _str="Check for the presence or absence of liquidated damages clauses that predefine compensation for breaches."
        _comment="Description of the liquidated damages clause."
      />
    ),
  },
];

const normalizeOptions = (options: Option[]) =>
  options.reduce<Record<string, Option>>((acc, option) => {
    acc[option.value] = option;
    return acc;
  }, {});

const normalizedOptions: Record<Option['value'], Option> = normalizeOptions(options);

const CustomOption = (props: any) => {
  const { innerProps, children, data: customOption } = props;

  return (
    <div
      className={style.CustomOption}
      {...innerProps}
    >
      <span>{children}</span>
      <p className={style.CustomOptionDescription}>{customOption.description}</p>
    </div>
  );
};

const showFeedbackToast = (isAddingNewClause: boolean) => {
  if (isAddingNewClause) {
    toast.success({
      id: 'clause-added',
      title: (
        <T
          _str="Clause added"
          _comment="Toast message when a clause is successfully added."
        />
      ),
      duration: 5000,
    });
  } else {
    toast.success({
      id: 'clause-removed',
      title: (
        <T
          _str="Clause removed"
          _comment="Toast message when a clause is successfully removed."
        />
      ),
      duration: 5000,
    });
  }
};

export const ClauseSelect = ({ isEditMode }: { isEditMode: boolean }) => {
  const t = useT();
  const setPlaybookValues = usePlaybookStore((state) => state.setPlaybookValues);
  const playbookValues = usePlaybookStore((state) => state.playbookValues);
  const value = useMemo(
    () =>
      Object.entries(
        playbookValues as Record<
          string,
          {
            enabled: boolean;
          }
        >,
      )
        .filter(([, values]) => values.enabled)
        .map(([key]) => ({
          value: key,
          label: normalizedOptions[key].label,
        })),
    [
      playbookValues,
    ],
  );
  const selectedOptionsCount = useRef(value.length);
  const onChange = useCallback(
    (selectedOptions: Option[]) => {
      const normalizedSelectedOptions = normalizeOptions(selectedOptions);
      const updatedPlaybookValues = options.reduce<
        Record<
          string,
          {
            enabled: boolean;
          }
        >
      >((acc, option) => {
        const isSelected = Boolean(normalizedSelectedOptions[option.value]);
        acc[option.value] = {
          enabled: isSelected,
        };
        return acc;
      }, {});
      setPlaybookValues(updatedPlaybookValues);

      showFeedbackToast(selectedOptions.length > selectedOptionsCount.current);
      selectedOptionsCount.current = selectedOptions.length;
    },
    [
      setPlaybookValues,
    ],
  );

  return (
    <SelectField
      multi
      disabled={!isEditMode}
      hideErrorElement
      options={options}
      placeholder={t('Select and add clauses to be used for AI review', {
        _comment: 'Placeholder for the select field in the AI Review page.',
      })}
      components={{
        Option: CustomOption,
      }}
      input={{
        value,
        name: 'clauses',
        onChange,
      }}
      labelKey="label"
    />
  );
};
